import React from 'react';
import { Link } from 'react-router-dom';
import { MdDoubleArrow } from 'react-icons/md';
import { FaLeaf, FaBrain, FaBalanceScale } from 'react-icons/fa';
import WellnessImage from '../../assets/images/people/siv-renate-main-about-me-in-beautiful-norway.webp';
import { BOOKING_PAGE_URL, CONTACT_PAGE_URL } from '../../utils/Routes';

function AboutSivSection() {
  return (
    <section className='grid gap-y-8 px-8 py-8 xl:py-24 2xl:px-32 lg:px-16'>
      {/* Heading */}
      <div className='grid gap-y-4 lg:gap-y-6 justify-center'>
        <h3 className='font-titleFont text-2xl text-center border-b-2 border-solid border-colour2 dark:border-colour3 pb-2'>
          ABOUT SIV RENATE
        </h3>
        <h4 className='text-center lg:text-xl italic font-serif'>
          UGC Creator & Wellness Coach · Drammen, Norway
        </h4>
      </div>

      {/* Main: Bio + Image */}
      <div className='grid lg:grid-cols-2 gap-8 dark:text-colour2'>
        {/* Bio + Benefits + CTA */}
        <section className='grid gap-6 bg-colour1 rounded-3xl shadow-md p-6'>
          <article className='grid gap-4'>
            <h5 className='text-xl font-semibold font-titleFont'>
              My Story & Approach
            </h5>
            <p className='text-base opacity-90'>
              I’m <span className='font-semibold'>Siv Renate Skaufel</span>, a
              creator and coach from Drammen, Norway. I craft commercials and{' '}
              <span className='font-semibold'>UGC</span> that put real stories
              first, and I support clients
              <span className='font-semibold'> worldwide</span> with personal
              and mental wellness coaching. With a background in{' '}
              <span className='font-semibold'>media editing</span> and{' '}
              <span className='font-semibold'>storytelling</span>, my work
              blends creativity with evidence-informed guidance so you can
              reduce stress, gain clarity, and build balance that lasts.
            </p>
          </article>

          {/* Benefits */}
          <ul className='grid gap-4'>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaLeaf className='text-colour5 text-xl' aria-hidden='true' />
              <div className='grid'>
                <span className='font-semibold'>Stress Relief</span>
                <span className='text-sm opacity-80'>
                  Gentle grounding practices that calm your nervous system and
                  ease daily pressure.
                </span>
              </div>
            </li>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaBrain className='text-colour5 text-xl' aria-hidden='true' />
              <div className='grid'>
                <span className='font-semibold'>Clarity</span>
                <span className='text-sm opacity-80'>
                  Clear next steps and simple structure so you can focus on what
                  matters.
                </span>
              </div>
            </li>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaBalanceScale
                className='text-colour5 text-xl'
                aria-hidden='true'
              />
              <div className='grid'>
                <span className='font-semibold'>Balance</span>
                <span className='text-sm opacity-80'>
                  Rhythms that support your work, relationships, and personal
                  energy.
                </span>
              </div>
            </li>
          </ul>

          {/* CTA: Book coaching */}
          <div className='grid px-0 justify-center'>
            <Link
              to={BOOKING_PAGE_URL}
              className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
              aria-label='Book a session with Siv Renate'
              title='Book a coaching session with Siv Renate'
              rel='bookmark'
            >
              <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
                <span>BOOK A SESSION</span>
                <MdDoubleArrow
                  className='text-xl lg:text-2xl'
                  aria-hidden='true'
                />
              </div>
            </Link>
          </div>
        </section>

        {/* Image */}
        <section className='grid rounded-3xl overflow-hidden shadow-md'>
          <img
            src={WellnessImage}
            alt='Siv Renate Skaufel — UGC creator and wellness coach in Drammen, Norway'
            className='w-full h-full object-cover'
            loading='lazy'
            decoding='async'
          />
        </section>
      </div>

      {/* Coaching Offers (kept, copy rewritten) */}
      <div className='grid gap-6 pt-8'>
        <h5 className='text-xl font-semibold font-titleFont text-center'>
          My Journey & Values
        </h5>

        <div className='grid lg:grid-cols-3 gap-6'>
          {/* Card 1 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Background</h6>
              <span className='text-sm italic text-colour5 opacity-80'>
                From Drammen, Norway
              </span>
            </div>
            <p className='text-sm opacity-90'>
              I grew up in Drammen and built a career blending{' '}
              <span className='font-semibold'>creativity</span> with{' '}
              <span className='font-semibold'>well-being</span>. My path has
              taken me from media editing and storytelling into a practice where
              I combine professionalism with compassion.
            </p>
          </article>

          {/* Card 2 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Experience</h6>
              <span className='text-sm italic text-colour5 opacity-80'>
                UGC & Commercials
              </span>
            </div>
            <p className='text-sm opacity-90'>
              Over the years I’ve created{' '}
              <span className='font-semibold'>commercials</span> and{' '}
              <span className='font-semibold'>UGC</span> for clients worldwide.
              My work focuses on authentic stories that connect, supported by
              strong
              <span className='font-semibold'> media editing</span> skills and a
              modern creative style.
            </p>
          </article>

          {/* Card 3 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Philosophy</h6>
              <span className='text-sm italic text-colour5 opacity-80'>
                Balance & Clarity
              </span>
            </div>
            <p className='text-sm opacity-90'>
              I believe in bringing{' '}
              <span className='font-semibold'>
                clarity, balance, and purpose
              </span>{' '}
              to both creative projects and personal growth. My goal is to
              create work that feels meaningful while supporting others in their
              own journeys.
            </p>
          </article>
        </div>

        {/* CTA: Contact for pricing */}
        <div className='grid px-0 justify-center'>
          <Link
            to={CONTACT_PAGE_URL}
            className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
            aria-label='Contact Siv Renate for pricing'
            title='Contact Siv Renate for pricing'
            rel='bookmark'
          >
            <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
              <span>EMAIL FOR PRICING</span>
              <MdDoubleArrow
                className='text-xl lg:text-2xl'
                aria-hidden='true'
              />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default AboutSivSection;
